import { useEffect, useState } from 'react';
import cx from 'classnames';
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Hidden,
  Collapse,
} from '@material-ui/core';

import { useGetCategoriesTreeQuery } from 'core/services';
import { getArray } from 'utils/helpers';
import useMobile from 'hooks/useMobile.hook';
import useIsLoading from 'hooks/useIsLoading.hook';
import Icon from 'components/Icon';

import NavigationContent from './NavigationContent';
import styles from './index.module.scss';
import Repeat from 'components/Repeat';
import { Skeleton } from '@material-ui/lab';
import Space from 'components/Space';
import AccountNavigation from '../AccountNavigation';

const categoryIcons = {
  'life-science': <Icon.DnaColor />,
  chemistry: <Icon.Flask />,
  'material-science': <Icon.Relations />,
  electronics: <Icon.Plug />,
  healthcare: <Icon.Heart color="error" />,
  labware: <Icon.TestTubes />,
  'lab-equipment': <Icon.Microscope />,
  'science-education': <Icon.Apple />,
  industrial: <Icon.Factory />,
  default: <Icon.DnaColor />,
};

const Navigation = ({ active, setActive }) => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const isMobile = useMobile();

  const queryCategories = useGetCategoriesTreeQuery(undefined, {
    skip: !active,
  });

  const loading = useIsLoading(queryCategories);
  const categoriesList = getArray(queryCategories.data?.categories);

  useEffect(() => {
    if (active) {
      setMounted(true);
      setOpen(true);
      setHidden(true);
      setCurrentCategory(isMobile ? null : categoriesList[0]?.id);
    } else {
      setOpen(false);
      setMounted(false);
      setTimeout(() => {
        setHidden(false);
      }, 300);
    }
  }, [active, categoriesList, isMobile]);

  return (
    <div
      className={cx(
        styles.navigation,
        mounted && styles.opacity,
        hidden && styles.visible,
      )}
    >
      <Drawer
        anchor="left"
        open={open}
        variant="persistent"
        className={styles.drawer}
      >
        <div className={styles.drawerWrapper}>
          <div className={styles.titleContainer}>
            <div className={styles.header}>
              <Hidden smUp>
                <AccountNavigation />
              </Hidden>
              <span className={styles.title}>Shop By Categories</span>
            </div>

            <IconButton
              size={isMobile ? 'small' : undefined}
              className={styles.close}
              onClick={() => {
                setActive(false);
              }}
            >
              <Icon.Close />
            </IconButton>
          </div>

          {loading && (
            <Space direction="column" width="100%" mt={2} p={1} gap={3}>
              <Repeat times={7}>
                <Skeleton variant="rect" width="100%" height={40} />
              </Repeat>
            </Space>
          )}

          {!loading && (
            <List component="nav" className={styles.nav}>
              {categoriesList
                .filter(
                  (cat) =>
                    cat.title !== 'Services' &&
                    cat.title !== 'Electronic Components',
                )
                .map((item) => {
                  return (
                    <Collapse
                      key={item.id}
                      in={
                        !isMobile ||
                        item.id === currentCategory ||
                        !currentCategory
                      }
                    >
                      <ListItem
                        disableGutters
                        className={cx(
                          styles.navItem,
                          item.id === currentCategory && styles.current,
                        )}
                      >
                        <Hidden smUp>
                          <ListItemIcon className={styles.iconBackContainer}>
                            <IconButton
                              size="small"
                              className={styles.iconLeft}
                              onClick={() => setCurrentCategory(null)}
                            >
                              <Icon.ArrowAngular />
                            </IconButton>
                          </ListItemIcon>
                        </Hidden>

                        <ListItemIcon className={styles.navItemIcon}>
                          {categoryIcons[item.slug] || categoryIcons.default}
                        </ListItemIcon>

                        <ListItemText
                          disableTypography
                          className={styles.navItemText}
                          primary={item.title}
                          onClick={() => {
                            if (isMobile) {
                              setCurrentCategory(
                                currentCategory ? null : item.id,
                              );
                            } else {
                              setCurrentCategory(item.id);
                            }
                          }}
                        />

                        <ListItemIcon
                          className={styles.navItemIconAfter}
                          onClick={() => {
                            if (isMobile) {
                              setCurrentCategory(
                                currentCategory ? null : item.id,
                              );
                            } else {
                              setCurrentCategory(item.id);
                            }
                          }}
                        >
                          <Icon.ArrowAngular />
                        </ListItemIcon>
                      </ListItem>
                    </Collapse>
                  );
                })}
            </List>
          )}

          <Hidden smUp>
            <NavigationContent
              styles={styles}
              setActive={setActive}
              categoriesList={categoriesList}
              currentCategory={currentCategory}
            />
          </Hidden>
        </div>
      </Drawer>

      <Hidden xsDown>
        <NavigationContent
          styles={styles}
          setActive={setActive}
          categoriesList={categoriesList}
          currentCategory={currentCategory}
        />
      </Hidden>
    </div>
  );
};

export default Navigation;
